/**
 * App Management — remote feature flags. Lets staff hide individual features
 * (a Power button) or whole screens of the mobile app without shipping an app
 * update. The app self-registers its feature manifest at startup, so a newly
 * gated feature appears in the admin panel on its own; unknown keys are always
 * visible, so every layer fails open.
 *
 *   GET  /app-config              — mobile: flags already resolved for the caller
 *   POST /app-config/register     — mobile: upsert the manifest this build ships
 *   GET  /admin/app-features      — admin: every flag with its targeting
 *   PATCH /admin/app-features/:key— admin (super_admin): visibility + targeting
 *   DELETE /admin/app-features/:key — admin (super_admin): drop a stale key
 *
 * Visibility semantics (`visible` × `targetingMode` × targets):
 *   visible=true                          → shown to everyone; targets ignored
 *                                           and cleared on save.
 *   visible=false, hide_selected, no targets → hidden for EVERYONE.
 *   visible=false, hide_selected, targets    → hidden only for a matching user
 *                                           or role, and (device targets) only
 *                                           on those devices.
 *   visible=false, show_selected, targets    → hidden for everyone EXCEPT a
 *                                           matching user/role (visible
 *                                           everywhere for them) or on a
 *                                           matching device.
 *   visible=false, show_selected, no targets → hidden for EVERYONE.
 *
 * Targeting is resolved server-side: the mobile client holds no user id, so it
 * receives an already-personalised deny list.
 */
import { z } from 'zod';
import { UuidSchema } from '../common/id.js';
import { Iso8601Schema } from '../common/timestamp.js';
import { DeviceKindSchema } from '../common/device-kind.js';
import { AppRoleSchema } from '../auth/roles.js';
/** `feature` hides one widget; `screen` also blocks the route (redirect). */
export const AppFeatureKindSchema = z.enum(['feature', 'screen']);
/** Product family a flag belongs to. `global` = not tied to one product. */
export const AppFeatureProductSchema = z.enum([...DeviceKindSchema.options, 'global']);
/** Dotted key, e.g. `stair.control.power`. Stable across app versions. */
export const AppFeatureKeySchema = z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9]+(?:[._][a-z0-9]+)*$/);
export const AppFeatureTargetingModeSchema = z.enum(['hide_selected', 'show_selected']);
// ── GET /app-config ──────────────────────────────────────────────────────
/**
 * Small remote-tunable app settings, distinct from feature VISIBILITY.
 * Stored in `app_settings` (key/value), edited by super_admin via
 * PATCH /admin/app-settings, delivered to the app inside GET /app-config.
 * Every field is optional — an absent value means "use the built-in default"
 * (stated per field), so the app never breaks on an older backend.
 */
export const AppSettingsSchema = z.object({
    /** Days a soft-deleted ceiling pattern stays restorable before permanent
     *  purge. Default 30. The backend enforces it on config save; the app
     *  filters expired entries out of every list. */
    ceilingPatternRetentionDays: z.number().int().min(1).max(365).optional(),
});
/**
 * `hiddenPerDevice` holds the COMPLETE hidden set for each of the caller's
 * devices — not a delta on `hiddenKeys` — so a flag can be visible on one
 * device and hidden on another. `hiddenKeys` applies where no device is in
 * context (setup flows, global screens) and to any device absent from the map.
 */
export const AppConfigResponse = z.object({
    hiddenKeys: z.array(AppFeatureKeySchema),
    hiddenPerDevice: z.record(UuidSchema, z.array(AppFeatureKeySchema)),
    /** Optional so an older backend's response still validates. */
    settings: AppSettingsSchema.optional(),
});
// ── GET/PATCH /admin/app-settings ────────────────────────────────────────
export const AdminAppSettingsResponse = z.object({
    settings: AppSettingsSchema,
});
/** Partial update — only the provided keys change. */
export const AdminAppSettingsUpdateBody = AppSettingsSchema;
// ── POST /app-config/register ────────────────────────────────────────────
export const AppFeatureRegistrationSchema = z.object({
    key: AppFeatureKeySchema,
    product: AppFeatureProductSchema,
    screen: z.string().min(1).max(120),
    label: z.string().min(1).max(200),
    description: z.string().max(1000).optional(),
    kind: AppFeatureKindSchema,
    sortOrder: z.number().int().default(0),
    /** Safety-relevant (lift Emergency/Stop) — the panel warns before hiding. */
    critical: z.boolean().default(false),
});
export const AppConfigRegisterBody = z.object({
    appVersion: z.string().min(1).max(40),
    features: z.array(AppFeatureRegistrationSchema).min(1).max(500),
});
export const AppConfigRegisterResponse = z.object({
    total: z.number().int(),
    created: z.number().int(),
});
// ── GET /admin/app-features ──────────────────────────────────────────────
export const AppFeatureTargetsSchema = z.object({
    devices: z.array(UuidSchema),
    roles: z.array(AppRoleSchema),
    users: z.array(UuidSchema),
});
export const AdminAppFeatureSchema = z.object({
    key: AppFeatureKeySchema,
    product: AppFeatureProductSchema,
    screen: z.string(),
    label: z.string(),
    description: z.string().nullable(),
    kind: AppFeatureKindSchema,
    visible: z.boolean(),
    targetingMode: AppFeatureTargetingModeSchema,
    critical: z.boolean(),
    sortOrder: z.number().int(),
    firstSeenAppVersion: z.string().nullable(),
    lastRegisteredAppVersion: z.string().nullable(),
    updatedAt: Iso8601Schema,
    targets: AppFeatureTargetsSchema,
});
export const AdminAppFeatureListResponse = z.object({
    features: z.array(AdminAppFeatureSchema),
    /** Newest manifest version seen — a key not registered by it is stale. */
    latestAppVersion: z.string().nullable(),
});
// ── PATCH /admin/app-features/:key ───────────────────────────────────────
/** `targets`, when present, REPLACES the stored set for that flag. */
export const AdminAppFeatureUpdateBody = z.object({
    visible: z.boolean().optional(),
    targetingMode: AppFeatureTargetingModeSchema.optional(),
    targets: AppFeatureTargetsSchema.optional(),
});
//# sourceMappingURL=app-config.js.map